import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { getMyData, saveMyData } from "@/lib/api";

const BookmarkContext = createContext(null);
const KEY = "dharmartha_bookmarks";

export const BookmarkProvider = ({ children }) => {
  const { user } = useAuth();
  const [bookmarks, setBookmarks] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const saveTimer = useRef(null);

  const pushToServer = useCallback((next) => {
    if (!user) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveMyData({ bookmarks: next }).catch(() => {}); }, 900);
  }, [user]);

  // On login: merge server + local bookmarks (union) and sync back.
  useEffect(() => {
    if (!user) return;
    getMyData().then((d) => {
      const server = d.bookmarks || [];
      setBookmarks((local) => {
        const merged = Array.from(new Set([...server, ...local]));
        localStorage.setItem(KEY, JSON.stringify(merged));
        if (merged.length !== server.length) saveMyData({ bookmarks: merged }).catch(() => {});
        return merged;
      });
    }).catch(() => {});
  }, [user]);

  const persist = useCallback((next) => {
    setBookmarks(next);
    localStorage.setItem(KEY, JSON.stringify(next));
    pushToServer(next);
  }, [pushToServer]);

  const isBookmarked = useCallback((slug) => bookmarks.includes(slug), [bookmarks]);

  const toggleBookmark = useCallback((slug) => {
    setBookmarks((prev) => {
      const next = prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug];
      localStorage.setItem(KEY, JSON.stringify(next));
      pushToServer(next);
      return next;
    });
  }, [pushToServer]);

  return (
    <BookmarkContext.Provider value={{ bookmarks, isBookmarked, toggleBookmark, persist }}>
      {children}
    </BookmarkContext.Provider>
  );
};

export const useBookmarks = () => {
  const ctx = useContext(BookmarkContext);
  if (!ctx) throw new Error("useBookmarks must be used within BookmarkProvider");
  return ctx;
};
